import { useState, useEffect } from "react";
import api from "../../../api";
import { useNavigate } from "react-router-dom";
import {
  X,
  Edit3,
  Check,
  ArrowRightLeft,
  CheckCircle,
  AlertCircle,
  XCircle,
  User,
  Building2,
  MapPin,
  FileText,
  Target
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import CustomSelect from "../../../components/ui/CustomSelect";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/Input";
import { useNotification } from "../../../context/NotificationContext";
import Tooltip from "../../../components/ui/Tooltip";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
};

const formatPhone = (phone) => {
  if (!phone) return "-";
  const cleanPhone = phone.trim();
  if (cleanPhone.startsWith("+")) return cleanPhone;
  if (cleanPhone.startsWith("0")) return `+33 ${cleanPhone.substring(1)}`;
  return cleanPhone;
};

const InfoField = ({ label, value, name, type = "text", isHalf = false, rows = 3, required = false, isEditing, formData, handleChange, options, phoneCountryCode, setPhoneCountryCode }) => {
  if (isEditing) {
    return (
      <motion.div variants={itemVariants} className={isHalf ? "w-1/2" : "w-full"}>
        <label className="block text-xs font-semibold text-subtle mb-1">
          {label} {required && <span className="text-danger-500">*</span>}
        </label>
        {type === "textarea" ? (
          <div className="relative">
            <textarea
              name={name}
              value={formData[name] || ""}
              onChange={handleChange}
              rows={rows}
              maxLength={name === "interestNotes" ? 200 : undefined}
              className="flex w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] resize-none min-h-[60px]"
            />
            {name === "interestNotes" && (
              <span className="absolute bottom-2 right-3 text-[10px] text-muted pointer-events-none">
                {(formData[name] || "").length}/200
              </span>
            )}
          </div>
        ) : type === "select" && options ? (
          <CustomSelect
            value={formData[name] || ""}
            onChange={(val) => handleChange({ target: { name, value: val } })}
            options={options}
            className="w-full"
          />
        ) : type === "phone" ? (
          <div className="relative flex items-center">
            <div className="absolute inset-y-0 left-0 flex items-center">
              <select
                value={phoneCountryCode}
                onChange={(e) => setPhoneCountryCode(e.target.value)}
                className="h-full rounded-md border-0 bg-transparent py-0 pl-3 text-subtle focus:ring-2 focus:ring-inset focus:ring-accent sm:text-sm w-[95px] outline-none"
              >
                <option value="FR (+33)">FR (+33)</option>
                <option value="US (+1)">US (+1)</option>
                <option value="CA (+1)">CA (+1)</option>
                <option value="CH (+41)">CH (+41)</option>
                <option value="BE (+32)">BE (+32)</option>
                <option value="UK (+44)">UK (+44)</option>
                <option value="DE (+49)">DE (+49)</option>
                <option value="ES (+34)">ES (+34)</option>
                <option value="IT (+39)">IT (+39)</option>
              </select>
            </div>
            <Input
              name={name}
              value={formData[name] || ""}
              onChange={handleChange}
              className="pl-[105px]"
            />
          </div>
        ) : (
          <Input
            type={type}
            name={name}
            value={formData[name] || ""}
            onChange={handleChange}
          />
        )}
      </motion.div>
    );
  }

  return (
    <motion.div variants={itemVariants} className={isHalf ? "w-1/2" : "w-full"}>
      <dt className="text-[11px] uppercase tracking-wider font-semibold text-subtle mb-0.5">
        {label} {required && <span className="text-danger-500 text-sm leading-none">*</span>}
      </dt>
        <dd className={`text-[13px] text-primary font-medium bg-surface-muted px-2.5 py-1.5 rounded-md leading-tight whitespace-pre-wrap break-words ${type === "textarea" ? "min-h-[60px]" : "min-h-[28px]"}`}>
          {name === "linkedinUrl" && value ? (
            <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold flex items-center gap-1.5 w-max">
              <svg className="w-3.5 h-3.5 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              {formData.firstName || ""} {formData.lastName || "LinkedIn"}
            </a>
          ) : type === "phone" && value ? (
            formatPhone(value)
          ) : value ? (
            value
          ) : (
            <span className="text-muted italic font-normal text-[12px]">Non renseigné</span>
          )}
        </dd>
    </motion.div>
  );
};

export default function LeadDetail({ lead, onClose, refreshLeads, onTransformSuccess }) {
  const [formData, setFormData] = useState(lead);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transformed, setTransformed] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { showToast } = useNotification();
  const navigate = useNavigate();



  const [phoneCountryCode, setPhoneCountryCode] = useState("FR (+33)");

  useEffect(() => {
    let initialPhone = lead?.phone || "";
    let initialCountry = "FR (+33)";
    const prefixes = [
      { code: "FR (+33)", prefix: "+33" },
      { code: "US (+1)", prefix: "+1" },
      { code: "CA (+1)", prefix: "+1" },
      { code: "CH (+41)", prefix: "+41" },
      { code: "BE (+32)", prefix: "+32" },
      { code: "UK (+44)", prefix: "+44" },
      { code: "DE (+49)", prefix: "+49" },
      { code: "ES (+34)", prefix: "+34" },
      { code: "IT (+39)", prefix: "+39" },
    ];
    for (const p of prefixes) {
      if (initialPhone.startsWith(p.prefix)) {
        initialCountry = p.code;
        initialPhone = initialPhone.replace(p.prefix, "").trim();
        break;
      }
    }
    setPhoneCountryCode(initialCountry);
    setFormData({ ...lead, phone: initialPhone });
  }, [lead]);



  const handleChange = (e) => {
    let val = e.target.value;
    if (e.target.name === "phone") {
      val = val.replace(/[^\d\s]/g, "");
    }
    setFormData({ ...formData, [e.target.name]: val });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const prefixMatch = phoneCountryCode.match(/\(([^)]+)\)/);
      const prefix = prefixMatch ? prefixMatch[1] : "";
      
      const payload = {
        ...formData,
        phone: formData.phone ? `${prefix} ${formData.phone.trim()}`.trim() : ""
      };

      await api.put(`/leads/${formData.id || formData._id}`, payload);
      showToast("Informations mises à jour !");
      refreshLeads();
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la mise à jour.", "error");
    } finally {
      setLoading(false);
    }
  };

  const requiredFields = [
    "lastName", "firstName", "email", "phone", "position",
    "source", "channel", "interestNotes",
    "address", "postalCode", "city", "country"
  ];
  const missingFields = requiredFields.filter(field => !formData[field] || formData[field].toString().trim() === "");
  const isComplete = missingFields.length === 0;
  
  const isPM = !!formData.organizationName;

  const validateBeforeTransform = () => {
    if (!isComplete) {
      showToast("Veuillez remplir tous les champs Général (sauf LinkedIn) et Localisation pour transformer le lead.", "error");
      return false;
    }
    return true;
  };

  const handleTransformClick = () => {
    if (validateBeforeTransform()) {
      setShowConfirmModal(true);
    }
  };

  const executeTransform = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await api.post(`/leads/transform/${formData.id || formData._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTransformed(true);
      refreshLeads();
      if (onTransformSuccess) {
        onTransformSuccess();
      }
      onClose();
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Erreur lors de la transformation.", "error");
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };



  return (
    <div
      className="relative bg-surface w-full h-full shadow-hp-lg flex flex-col overflow-hidden rounded-xl border border-workspace"
    >
      {/* Branded Header */}
      <div
        className="px-6 py-8 relative overflow-hidden shrink-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/assets/subparts/subpart_5.png')" }}
      >
        <div className="absolute inset-0 bg-overlay backdrop-blur-[2px]" />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Badge Complet/Incomplet */}
        <div className="absolute top-4 right-12 pr-2 overflow-hidden flex items-center justify-center h-8">
          <AnimatePresence mode="wait">
            {isComplete ? (
              <motion.span
                key="complet"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-100 text-xs font-semibold border border-emerald-500/30 backdrop-blur-md whitespace-nowrap shadow-sm"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Complet
              </motion.span>
            ) : (
              <motion.span
                key="incomplet"
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warning-500/30 text-warning-100 text-xs font-semibold border border-warning-500/40 backdrop-blur-md whitespace-nowrap shadow-sm"
              >
                <AlertCircle className="w-3.5 h-3.5" />
                Incomplet
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        <div className="relative z-10 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white backdrop-blur-md border border-white/20 shadow-sm">
            {formData.organizationName ? (
              <Building2 className="w-6 h-6" />
            ) : (
              <User className="w-6 h-6" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {formData.lastName} {formData.firstName}
            </h2>
            <p className="text-white/80 text-sm mt-0.5">
              {formData.status || "Lead"} • {formData.organizationName ? formData.organizationName : "Personne Physique"}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-workspace px-2 shrink-0 overflow-x-auto">
        <button
          onClick={() => setActiveTab("general")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === "general" ? "border-primary text-primary" : "border-transparent text-subtle hover:text-primary"}`}
        >
          Général
        </button>
        <button
          onClick={() => setActiveTab("localisation")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === "localisation" ? "border-primary text-primary" : "border-transparent text-subtle hover:text-primary"}`}
        >
          Localisation
        </button>
        <button
          onClick={() => setActiveTab("qualification")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === "qualification" ? "border-primary text-primary" : "border-transparent text-subtle hover:text-primary"}`}
        >
          Qualification & Suivi
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 pb-10">
        <motion.dl
          className="space-y-4"
          variants={containerVariants}
          initial="hidden"
          animate="show"
          key={isEditing ? "edit" : activeTab}
        >
          {/* TAB GENERAL */}
          {activeTab === "general" && (
            <div className="space-y-3">

              <div className="flex gap-3">
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Nom" name="lastName" value={formData.lastName} required isHalf />
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Prénom" name="firstName" value={formData.firstName} required isHalf />
              </div>

              <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Email" name="email" value={formData.email} type="email" required />

              <div className="flex gap-3">
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Téléphone" name="phone" type="phone" value={lead?.phone} phoneCountryCode={phoneCountryCode} setPhoneCountryCode={setPhoneCountryCode} isHalf />
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="LinkedIn" name="linkedinUrl" value={formData.linkedinUrl} isHalf />
              </div>
              <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Poste / Fonction" name="position" value={formData.position} required />

              <div className="pt-3 border-t border-workspace/60 space-y-3">
                <div className="flex gap-3">
                  <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Source d’acquisition" name="source" value={formData.source} required isHalf />
                  <InfoField 
                    isEditing={isEditing} 
                    formData={formData} 
                    handleChange={handleChange} 
                    label="Canal" 
                    name="channel" 
                    value={formData.channel} 
                    type="select" 
                    options={[
                      { value: "PENDING", label: "En attente" },
                      { value: "INBOUND", label: "Entrant (Inbound)" },
                      { value: "OUTBOUND", label: "Sortant (Outbound)" },
                      { value: "REFERRAL", label: "Recommandation" },
                      { value: "EVENT", label: "Événement / Salon" },
                      { value: "DIRECT", label: "Direct" },
                      { value: "LINKEDIN", label: "LinkedIn" },
                      { value: "EMAIL", label: "Emailing" },
                      { value: "PHONE", label: "Téléphone" }
                    ]} 
                    required 
                    isHalf 
                  />
                </div>

                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Commentaire" name="interestNotes" value={formData.interestNotes} type="textarea" rows={6} required />

                {!isEditing && formData.createdAt && (
                  <motion.div variants={itemVariants} className="w-full pt-1">
                    <dt className="text-[11px] uppercase tracking-wider font-semibold text-subtle mb-0.5">Date d'ajout</dt>
                    <dd className="text-[13px] text-primary font-medium bg-surface-muted px-2.5 py-1.5 rounded-md min-h-[28px] leading-tight">
                      {new Date(formData.createdAt).toLocaleDateString("fr-FR", { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </dd>
                  </motion.div>
                )}
              </div>
            </div>
          )}

          {/* TAB LOCALISATION */}
          {activeTab === "localisation" && (
            <div className="space-y-3">
              <div className="pt-1"></div>
              <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Adresse" name="address" value={formData.address} required />
              <div className="flex gap-3">
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Code Postal" name="postalCode" value={formData.postalCode} required isHalf />
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Ville" name="city" value={formData.city} required isHalf />
              </div>
              <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Pays" name="country" value={formData.country} required />
            </div>
          )}

          {/* TAB QUALIFICATION */}
          {activeTab === "qualification" && (
            <div className="space-y-3">
              <div className="pt-1"></div>

              {isPM && (
                <>
                  <div className="flex gap-3">
                    <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Dénomination sociale" name="organizationName" value={formData.organizationName} isHalf />
                    <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="SIRET" name="siret" value={formData.siret} isHalf />
                  </div>

                  <div className="flex gap-3">
                    <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Secteur d'activité" name="sector" value={formData.sector} isHalf />
                    <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Patrimoine estimé" name="estimatedWealthBracket" value={formData.estimatedWealthBracket} isHalf />
                  </div>
                </>
              )}

              {!isPM && (
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Patrimoine estimé" name="estimatedWealthBracket" value={formData.estimatedWealthBracket} />
              )}

              <div className="flex gap-3">
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Score" name="score" value={formData.score} type="number" isHalf />
                <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Assigné à (ID)" name="ownerMembershipId" value={formData.ownerMembershipId} isHalf />
              </div>

              <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Apporteur d'affaires (ID)" name="introducerPartyId" value={formData.introducerPartyId} />

              <InfoField isEditing={isEditing} formData={formData} handleChange={handleChange} label="Raison de disqualification" name="disqualifiedReason" value={formData.disqualifiedReason} type="textarea" />
            </div>
          )}
        </motion.dl>
      </div>

      {/* Sticky Footer */}
      <div className="h-[72px] px-4 border-t border-workspace bg-surface shrink-0 flex items-center justify-between gap-3">
        {isEditing ? (
          <div className="flex gap-2 w-full">
            <Button variant="secondary" className="flex-1" onClick={() => setIsEditing(false)}>
              Annuler
            </Button>
            <Button variant="default" className="flex-1" onClick={handleSave} disabled={loading}>
              <Check className="w-4 h-4 mr-2" />
              {loading ? "Enregistrement..." : "Sauvegarder"}
            </Button>
          </div>
        ) : (
          <>
            <Button variant="secondary" className="flex-1" onClick={() => setIsEditing(true)}>
              <Edit3 className="w-4 h-4 mr-2" />
              Modifier
            </Button>

            {!transformed ? (
              <div className="flex-1 flex relative">
                <Tooltip content={!isComplete ? "Remplir les champs obligatoires *" : null} className="w-full">
                  <Button variant="accent" className="w-full" onClick={handleTransformClick} disabled={loading || !isComplete}>
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transformer
                  </Button>
                </Tooltip>
              </div>
            ) : (
              <Button variant="default" className="flex-1" onClick={() => navigate("/prospects/prospects")}>
                Voir Prospects
              </Button>
            )}
          </>
        )}
      </div>

      {/* Confirm Transform Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="w-16 h-16 rounded-full bg-accent-100 text-accent flex items-center justify-center mb-6 shadow-sm">
                <ArrowRightLeft className="w-8 h-8" />
              </div>

              <h3 className="text-xl font-bold text-primary mb-2">Transformer ce lead ?</h3>
              <p className="text-subtle mb-8">
                Êtes-vous sûr de vouloir transformer <strong>{formData.firstName} {formData.lastName}</strong> en prospect ?
              </p>

              <div className="flex gap-3 w-full">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={loading}
                >
                  Annuler
                </Button>
                <Button
                  variant="accent"
                  className="flex-1"
                  onClick={executeTransform}
                  disabled={loading}
                >
                  {loading ? "Chargement..." : "Confirmer"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
